import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Context Providers
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import NewReceiptPage from './pages/NewReceiptPage';
import ReceiptsListPage from './pages/ReceiptsListPage';
import StatisticsPage from './pages/StatisticsPage';
import UsersPage from './pages/UsersPage';
import ReportsPage from './pages/ReportsPage';
import InventoryPage from './pages/InventoryPage';

// Components
import Layout from './components/Layout';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, anyAdminOnly = false, adminOrInventory = false, noInventoryAdmin = false }) => {
  const { user, loading, isAdmin, isAnyAdmin, isAdminOrInventory } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-10 w-10 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (anyAdminOnly && !isAnyAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (adminOrInventory && !isAdminOrInventory()) {
    return <Navigate to="/dashboard" replace />;
  }

  // Block inventory_admin from certain pages — redirect to inventory
  if (noInventoryAdmin && user?.role === 'inventory_admin') {
    return <Navigate to="/inventory" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-10 w-10 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    // Inventory admin goes to inventory, others to dashboard
    if (user.role === 'inventory_admin') {
      return <Navigate to="/inventory" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Smart default redirect based on role
const DefaultRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-10 w-10 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'inventory_admin') return <Navigate to="/inventory" replace />;
  return <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />

      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute noInventoryAdmin>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      {/* Protected Routes - blocked for inventory_admin */}
      <Route 
        path="/new-receipt" 
        element={
          <ProtectedRoute noInventoryAdmin>
            <NewReceiptPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/receipts" 
        element={
          <ProtectedRoute noInventoryAdmin>
            <ReceiptsListPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin or Admin Role Routes */}
      <Route 
        path="/statistics" 
        element={
          <ProtectedRoute anyAdminOnly>
            <StatisticsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute anyAdminOnly>
            <ReportsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inventory" 
        element={
          <ProtectedRoute adminOrInventory>
            <InventoryPage />
          </ProtectedRoute>
        } 
      />

      {/* Admin or Admin Role - Users Management */}
      <Route 
        path="/users" 
        element={
          <ProtectedRoute anyAdminOnly>
            <UsersPage />
          </ProtectedRoute>
        } 
      />

      {/* Default Redirect */}
      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
